import type { CurrencyCode } from '@/types'

import { CURRENCIES } from '@/constants/carwash'

export const MINUTE = 60_000
export const HOUR = 60 * MINUTE
export const DAY = 24 * HOUR

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

type DateInput = string | number | Date

const pad = (value: number, size = 2): string => String(value).padStart(size, '0')

export const toDate = (value: DateInput): Date => value instanceof Date ? value : new Date(value)

export const toTime = (value: DateInput): number => toDate(value).getTime()

export const startOfDay = (value: DateInput): Date => {
    const date = new Date(toTime(value))
    date.setHours(0, 0, 0, 0)
    return date
}

export const addDays = (value: DateInput, days: number): Date => {
    const date = new Date(toTime(value))
    date.setDate(date.getDate() + days)
    return date
}

export const startOfWeek = (value: DateInput): Date => {
    const date = startOfDay(value)
    const offset = (date.getDay() + 6) % 7
    date.setDate(date.getDate() - offset)
    return date
}

export const startOfMonth = (value: DateInput): Date => {
    const date = startOfDay(value)
    date.setDate(1)
    return date
}

export const addMonths = (value: DateInput, months: number): Date => {
    const date = startOfMonth(value)
    date.setMonth(date.getMonth() + months)
    return date
}

export const isSameDay = (a: DateInput, b: DateInput): boolean => startOfDay(a).getTime() === startOfDay(b).getTime()

export const dayKey = (value: DateInput): string => {
    const date = toDate(value)
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const fromDayKey = (key: string): Date => {
    const [year, month, day] = key.split('-').map(Number)
    return new Date(year, (month || 1) - 1, day || 1)
}

export const formatTime = (value: DateInput): string => {
    const date = toDate(value)
    const hours = date.getHours()
    const hour12 = hours % 12 === 0 ? 12 : hours % 12
    return `${hour12}:${pad(date.getMinutes())} ${hours < 12 ? 'AM' : 'PM'}`
}

export const formatHourLabel = (hour: number): string => {
    const hour12 = hour % 12 === 0 ? 12 : hour % 12
    return `${hour12} ${hour < 12 ? 'AM' : 'PM'}`
}

export const formatDate = (value: DateInput): string => {
    const date = toDate(value)
    return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
}

export const formatShortDate = (value: DateInput): string => {
    const date = toDate(value)
    return `${date.getDate()} ${MONTHS[date.getMonth()]}`
}

export const formatWeekdayDate = (value: DateInput): string => {
    const date = toDate(value)
    return `${WEEKDAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]}`
}

export const formatLongDate = (value: DateInput): string => {
    const date = toDate(value)
    return `${WEEKDAYS_LONG[date.getDay()]}, ${date.getDate()} ${MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}`
}

export const formatMonth = (value: DateInput, long = false): string => {
    const date = toDate(value)
    return long ? `${MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}` : `${MONTHS[date.getMonth()]} ${String(date.getFullYear()).slice(2)}`
}

export const formatWeekday = (value: DateInput): string => WEEKDAYS[toDate(value).getDay()]

export const formatDateTime = (value: DateInput): string => `${formatDate(value)}, ${formatTime(value)}`

export const formatDayLabel = (value: DateInput, now: number): string => {
    const difference = Math.round((startOfDay(now).getTime() - startOfDay(value).getTime()) / DAY)

    if (difference === 0) return 'Today'
    if (difference === 1) return 'Yesterday'

    const date = toDate(value)
    return date.getFullYear() === new Date(now).getFullYear() ? formatWeekdayDate(date) : formatDate(date)
}

export const formatRelativeTime = (value: DateInput, now: number): string => {
    const minutes = Math.floor((now - toTime(value)) / MINUTE)

    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes} min ago`

    const hours = Math.floor(minutes / 60)
    if (hours < 24 && isSameDay(value, now)) return `${hours} h ago`

    return `${formatDayLabel(value, now)}, ${formatTime(value)}`
}

export const formatClock = (milliseconds: number): string => {
    const total = Math.max(0, Math.floor(milliseconds / 1000))
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export const formatMinutes = (value: number): string => {
    const minutes = Math.max(0, Math.round(value))

    if (minutes < 60) return `${minutes} min`

    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest ? `${hours} h ${rest} min` : `${hours} h`
}

export const formatDuration = (milliseconds: number): string => formatMinutes(milliseconds / MINUTE)

/* Money ------------------------------------------------------------ */

const moneyFormatters = new Map<string, Intl.NumberFormat>()

export const getCurrencyMeta = (code: CurrencyCode) => CURRENCIES.find(currency => currency.code === code) ?? CURRENCIES[0]

const getMoneyFormatter = (currency: CurrencyCode, decimals: number): Intl.NumberFormat => {
    const meta = getCurrencyMeta(currency)
    const key = `${meta.code}:${decimals}`
    let formatter = moneyFormatters.get(key)

    if (!formatter) {
        try {
            formatter = new Intl.NumberFormat(meta.locale, {
                style: 'currency',
                currency: meta.code,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            })
        }
        catch {
            formatter = new Intl.NumberFormat('en', {
                style: 'currency',
                currency: meta.code,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            })
        }

        moneyFormatters.set(key, formatter)
    }

    return formatter
}

export const roundMoney = (value: number): number => Math.round((Number.isFinite(value) ? value : 0) * 1000) / 1000

export const formatMoney = (amount: number, currency: CurrencyCode = 'INR'): string => {
    const meta = getCurrencyMeta(currency)
    const whole = Math.abs(amount - Math.round(amount)) < 0.0005
    return getMoneyFormatter(currency, whole ? 0 : meta.decimals).format(amount)
}

export const getCurrencySymbol = (currency: CurrencyCode): string => {
    const meta = getCurrencyMeta(currency)

    try {
        const parts = new Intl.NumberFormat(meta.locale, {
            style: 'currency',
            currency: meta.code,
            currencyDisplay: 'narrowSymbol'
        }).formatToParts(0)

        return parts.find(part => part.type === 'currency')?.value ?? meta.code
    }
    catch {
        return meta.code
    }
}

const trimNumber = (value: number): string => {
    const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10
    return String(rounded)
}

export const formatMoneyCompact = (amount: number, currency: CurrencyCode = 'INR'): string => {
    const symbol = getCurrencySymbol(currency)
    const prefix = /^[A-Za-z]+$/.test(symbol) ? `${symbol} ` : symbol
    const value = Math.abs(amount)
    const sign = amount < 0 ? '-' : ''

    if (currency === 'INR' && value >= 10_000_000) return `${sign}${prefix}${trimNumber(value / 10_000_000)}Cr`
    if (currency === 'INR' && value >= 100_000) return `${sign}${prefix}${trimNumber(value / 100_000)}L`
    if (value >= 1000) return `${sign}${prefix}${trimNumber(value / 1000)}k`

    return `${sign}${prefix}${Math.round(value)}`
}

const numberFormatter = new Intl.NumberFormat('en-IN')

export const formatNumber = (value: number): string => numberFormatter.format(value)

export const formatPercent = (value: number): string => `${Math.round(value)}%`

export const pluralize = (count: number, singular: string, plural = `${singular}s`): string => `${formatNumber(count)} ${count === 1 ? singular : plural}`
